"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Attempt = void 0;
var findLast_1 = __importDefault(require("lodash/findLast"));
var isEmpty_1 = __importDefault(require("lodash/isEmpty"));
var constants_1 = require("../constants");
/**
 * Attempt representing attempt class information.
 *
 * @property {string}           id            - identifier for the attempt
 * @property {Assessment}       assessment    - associated assessment
 * @property {number}           score         - score obtained in the attempt
 * @property {Date}             startedAt     - start date
 * @property {AttemptStatus}    status        - attempt status, see the possible values in @AttemptStatus
 * @property {string}           assignee      - the user assigned to this attempt
 * @property {number}           maxScore      - attempt max score
 * @property {Date}             updatedAt     - indicates when the attempt was last updated by the student (attempted an item or submitted)
 * @property {Date}             closedAt      - indicates when the attempt was closed by the student (submitted) or teacher
 * @property {ItemResponse[]}   responses     - attempt item responses
 * @property {Feedback[]}       feedback      - attempt feedback
 *
 * @author javier.perez
 */
var Attempt = /** @class */ (function () {
    function Attempt(data) {
        if (data === void 0) { data = {}; }
        Object.assign(this, data);
    }
    Object.defineProperty(Attempt.prototype, "isAssigned", {
        get: function () {
            return this.status === constants_1.AttemptStatus.Assigned;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isOpen", {
        get: function () {
            return this.status === constants_1.AttemptStatus.Open;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isClosed", {
        get: function () {
            return this.status === constants_1.AttemptStatus.Closed;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isInProgress", {
        get: function () {
            return this.status === constants_1.AttemptStatus.InProgress;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isSubmitted", {
        get: function () {
            return this.status === constants_1.AttemptStatus.Submitted;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isNotScored", {
        get: function () {
            var _a, _b;
            return ((_a = this.responses) === null || _a === void 0 ? void 0 : _a.length) > 0 && ((_b = this.responses) === null || _b === void 0 ? void 0 : _b.every(function (response) { return !response.isGraded; }));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isScored", {
        get: function () {
            var _a, _b;
            return (((_a = this.responses) === null || _a === void 0 ? void 0 : _a.length) > 0 &&
                ((_b = this.responses) === null || _b === void 0 ? void 0 : _b.every(function (response) {
                    return !response.isQuantitativeGraded ? response.isGraded : response.isQuantitativeGraded;
                })));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isScoredNoStudentSubmission", {
        get: function () {
            var _a, _b;
            return (((_a = this.responses) === null || _a === void 0 ? void 0 : _a.length) > 0 &&
                ((_b = this.responses) === null || _b === void 0 ? void 0 : _b.every(function (response) {
                    return response.isGradedNoStudentSubmission;
                })));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "wasStarted", {
        get: function () {
            return !!this.startedAt;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isNotCompleted", {
        get: function () {
            return this.wasStarted && !(0, isEmpty_1.default)(this.responses) && !this.isSubmitted;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "isDiagnosticAttempt", {
        get: function () {
            return this.assessment.type === constants_1.AssessmentType.Diagnostic;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "totalCorrect", {
        get: function () {
            var _this = this;
            var coreItems = this.getCoreItems();
            return (this.responses || []).filter(function (_a) {
                var isCorrect = _a.isCorrect, externalItemId = _a.externalItemId;
                var isCoreItem = !_this.isDiagnosticAttempt ||
                    coreItems.filter(function (_a) {
                        var externalId = _a.externalId;
                        return externalItemId === externalId;
                    });
                return isCorrect && isCoreItem;
            }).length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "totalIncorrect", {
        get: function () {
            return (this.responses || []).filter(function (response) { return response.isIncorrect && response.attempted; })
                .length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "totalSkipped", {
        get: function () {
            return (this.responses || []).filter(function (response) { return !response.attempted; }).length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "totalPending", {
        get: function () {
            return (this.responses || []).filter(function (response) { return response.attempted && !response.isQuantitativeGraded; }).length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "totalPartiallyCorrect", {
        get: function () {
            return (this.responses || []).filter(function (response) { return response.isPartiallyCorrect; }).length;
        },
        enumerable: false,
        configurable: true
    });
    Attempt.prototype.getDiagnosticScore = function () {
        var coreItems = this.getCoreItemIds();
        var disgnosticScore = !(0, isEmpty_1.default)(this.responses)
            ? this.responses
                .filter(function (response) { return coreItems.includes(response.externalItemId); })
                .map(function (response) { return response.totalScore; })
                .reduce(function (p, c) { return p + c; }, 0)
            : 0;
        return Number(disgnosticScore.toFixed(2));
    };
    Object.defineProperty(Attempt.prototype, "totalScore", {
        get: function () {
            return this.isDiagnosticAttempt
                ? this.getDiagnosticScore()
                : Number((this.score || 0).toFixed(2));
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Attempt.prototype, "calculatedMaxScore", {
        get: function () {
            return this.isDiagnosticAttempt
                ? this.getCoreItems()
                    .map(function (_a) {
                    var maxScore = _a.maxScore;
                    return maxScore;
                })
                    .reduce(function (acc, current) { return acc + current; }, 0)
                : this.maxScore;
        },
        enumerable: false,
        configurable: true
    });
    Attempt.prototype.getCoreItems = function () {
        var items = this.assessment.assessmentTemplate.items;
        return items.filter(function (item) { return !item.isBranch; });
    };
    Attempt.prototype.getCoreItemIds = function () {
        return this.getCoreItems().map(function (_a) {
            var externalId = _a.externalId;
            return externalId;
        });
    };
    /**
     * Returns the feedback provided for the item.
     * TODO once we support co teacher we need to pass the user id to this method
     * @param {Item} item
     * @returns {Feedback | undefined}
     */
    Attempt.prototype.getItemFeedback = function (item) {
        return (0, findLast_1.default)(this.feedback, function (feedback) { return feedback.itemExternalId === item.externalId; });
    };
    /**
     * Returns the item responses for the provided item
     * @param {Item} item
     * @returns {ItemResponse | undefined}
     */
    Attempt.prototype.getItemResponse = function (item) {
        return (this.responses || []).find(function (response) { return response.externalItemId === item.externalId; });
    };
    /**
     * Returns provided item exists in the item response
     * @param {Item} item
     * @returns boolean
     */
    Attempt.prototype.hasItemResponse = function (item) {
        return !!this.getItemResponse(item);
    };
    /**
     * Returns the general feedback
     * @returns {Feedback | undefined}
     */
    Attempt.prototype.getGeneralFeedback = function () {
        return (this.feedback || []).find(function (response) { return !response.itemExternalId; });
    };
    return Attempt;
}());
exports.Attempt = Attempt;
